<template>
  <div ref="container" class="fireworks-canvas"></div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import * as THREE from 'three';
import gsap from 'gsap';

const container = ref(null);
let scene, camera, renderer, particles = [];
let clock = new THREE.Clock();
let nextFireworkTime = 0;
let resizeTimeout;

onMounted(() => {
  init();
  animate();
  window.addEventListener('resize', onWindowResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', onWindowResize);
  if (resizeTimeout) clearTimeout(resizeTimeout);
  if (renderer) renderer.dispose();
});

const init = () => {
  scene = new THREE.Scene();
  
  // Aspect will be updated in onWindowResize
  camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
  camera.position.z = 20;

  renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  container.value.appendChild(renderer.domElement);
  
  // Initial size setup - immediate
  const width = window.innerWidth;
  const height = window.innerHeight;
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.setSize(width, height);
};

const createFirework = () => {
  const x = (Math.random() - 0.5) * 40;
  const y = (Math.random() - 0.5) * 20;
  const color = new THREE.Color().setHSL(Math.random(), 0.8, 0.6);
  const type = Math.floor(Math.random() * 3); // 0: Ball, 1: Ring, 2: Heart
  
  const particleCount = type === 2 ? 150 : 100; // More for heart
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(particleCount * 3);
  const velocities = [];

  for (let i = 0; i < particleCount; i++) {
    positions[i * 3] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = 0;
    
    let vx, vy, vz;
    const power = Math.random() * 0.1 + 0.05;

    if (type === 1) {
      // Ring type
      const angle = (i / particleCount) * Math.PI * 2;
      vx = Math.cos(angle) * power;
      vy = Math.sin(angle) * power;
      vz = (Math.random() - 0.5) * 0.02;
    } else if (type === 2) {
      // Heart type (parametric equation)
      const t = (i / particleCount) * Math.PI * 2;
      vx = (16 * Math.pow(Math.sin(t), 3)) / 150;
      vy = (13 * Math.cos(t) - 5 * Math.cos(2*t) - 2 * Math.cos(3*t) - Math.cos(4*t)) / 150;
      vz = (Math.random() - 0.5) * 0.02;
    } else {
      // Classic Ball
      const angle = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      vx = Math.sin(phi) * Math.cos(angle) * power;
      vy = Math.sin(phi) * Math.sin(angle) * power;
      vz = Math.cos(phi) * power * 0.5;
    }

    velocities.push({ x: vx, y: vy, z: vz });
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  
  const material = new THREE.PointsMaterial({
    size: 0.15,
    color: color,
    transparent: true,
    opacity: 1,
    blending: THREE.AdditiveBlending
  });

  const firework = new THREE.Points(geometry, material);
  scene.add(firework);
  
  particles.push({ mesh: firework, velocities, age: 0 });

  gsap.to(material, {
    opacity: 0,
    duration: 3,
    delay: 1,
    onComplete: () => {
      scene.remove(firework);
      particles = particles.filter(p => p.mesh !== firework);
    }
  });
};

const animate = () => {
  requestAnimationFrame(animate);
  const delta = clock.getDelta();
  const time = clock.elapsedTime;

  // Integrated firework trigger to avoid extra timers
  if (time > nextFireworkTime) {
    createFirework();
    nextFireworkTime = time + 2.5 + Math.random();
  }

  particles.forEach(p => {
    const pos = p.mesh.geometry.attributes.position.array;
    for (let i = 0; i < p.velocities.length; i++) {
      pos[i * 3] += p.velocities[i].x;
      pos[i * 3 + 1] += p.velocities[i].y;
      pos[i * 3 + 2] += p.velocities[i].z;
      
      // Gravity effect
      p.velocities[i].y -= 0.0005;
    }
    p.mesh.geometry.attributes.position.needsUpdate = true;
  });

  renderer.render(scene, camera);
};

const onWindowResize = () => {
  if (resizeTimeout) clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    requestAnimationFrame(() => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      if (camera && renderer) {
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height, false); // false to avoid redundant style updates if possible
      }
    });
  }, 150);
};
</script>

<style scoped>
.fireworks-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
</style>
